// Owner chat — entry: init views + composer wiring
import { getTg } from '../../core/telegram.js';
import { scheduleChatKeyboardInset } from '../../app/chat-keyboard.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import {
  getOwnerChatActiveConversationUserId,
  setOwnerChatActiveConversationUserId,
  getOwnerChatSelectedDisplayName,
  setOwnerChatSelectedDisplayName,
  getOwnerChatClearMode,
  setOwnerChatAutoScrollEnabled,
  getOwnerChatAutoScrollEnabled,
  getOwnerChatSuppressKeyboardScroll,
  getOwnerChatMode,
  setOwnerChatMode,
  resetOwnerChatOrderContext,
  getOwnerChatListTab,
  getOwnerChatActiveOrderId
} from './ui-state.js';
import { buildOrderChatThreadId } from '../../lib/order-chat-thread.js';
import {
  getOwnerChatViewerId,
  ownerChatMessagesEl,
  ownerChatComposerEl,
  ownerChatInputEl,
  ownerChatSendBtn,
  ownerChatAttachBtn,
  ownerChatPhotoInput,
  ownerChatAttachRemoveBtn,
  ownerChatSubtitleEl,
  ownerChatListSubtitleEl,
  ownerChatLoadingEl,
  ownerChatThreadsEl
} from './dom.js';
import { scrollOwnerChatToBottom, scrollOwnerChatToBottomSmooth } from './scroll.js';
import { resetOwnerChatLastSeenId, getOwnerChatIsSending } from './session.js';
import { ownerChatClearPendingPhoto, ownerChatSetPendingPhoto, ownerChatSyncSendButtonState } from './attach.js';
import { clearOwnerChatMessages, loadOwnerChatMessages } from './messages.js';
import { loadOwnerChatThreads, renderOwnerChatThreads, bindOwnerChatTabs, initOwnerChatTabsUi } from './threads.js';
import {
  stopOwnerChatPolling,
  subscribeOwnerChatThreads,
  subscribeOwnerChatConversation,
  startOwnerChatPollFallback
} from './websocket.js';
import { sendOwnerChatMessage } from './send.js';
import { bindOwnerChatComposerInput } from './composer-input.js';
import { bindOwnerChatMessageActions } from './message-actions.js';
import { cancelOwnerChatEdit } from './edit-mode.js';

bindOwnerChatComposerInput();
bindOwnerChatMessageActions();

export { clearOwnerChatMessages } from './messages.js';
export { scrollOwnerChatToBottom, scrollOwnerChatToBottomSmooth } from './scroll.js';

export async function initOwnerChatList() {
  if (!getTg()) return;
  if (!getIsOwnerApp()) return;
  if (!getOwnerChatViewerId()) return;

  stopOwnerChatPolling();
  ownerChatClearPendingPhoto(true);
  cancelOwnerChatEdit();
  setOwnerChatAutoScrollEnabled(false);
  resetOwnerChatLastSeenId();
  setOwnerChatActiveConversationUserId(null);
  setOwnerChatSelectedDisplayName('');
  clearOwnerChatMessages();

  if (ownerChatMessagesEl) ownerChatMessagesEl.style.display = 'none';
  if (ownerChatComposerEl) ownerChatComposerEl.style.display = 'none';
  if (ownerChatListSubtitleEl) {
    const tab = getOwnerChatListTab();
    if (getOwnerChatClearMode()) {
      ownerChatListSubtitleEl.textContent = 'Выберите чат для очистки истории';
    } else if (tab === 'order') {
      ownerChatListSubtitleEl.textContent = 'Чаты по заказам';
    } else {
      ownerChatListSubtitleEl.textContent = 'Консультация';
    }
  }

  if (ownerChatThreadsEl) ownerChatThreadsEl.style.display = 'block';

  initOwnerChatTabsUi();
  bindOwnerChatTabs(async function () {
    const threads = await loadOwnerChatThreads();
    renderOwnerChatThreads(threads);
  });

  const threads = await loadOwnerChatThreads();
  renderOwnerChatThreads(threads);

  subscribeOwnerChatThreads();
}

export async function initOwnerChatThread() {
  if (!getTg()) return;
  if (!getOwnerChatViewerId()) return;

  setOwnerChatAutoScrollEnabled(true);

  if (ownerChatInputEl) {
    ownerChatInputEl.placeholder = getIsOwnerApp() ? 'Ответ владельца...' : 'Сообщение...';
  }

  if (!ownerChatSubtitleEl) return;

  const backBtn = document.querySelector<HTMLElement>('#view-consult .back-btn');
  if (backBtn) {
    if (getIsOwnerApp()) {
      if (backBtn.dataset.backTo !== 'view-main') backBtn.dataset.backTo = 'view-owner-chat-list';
    } else {
      backBtn.dataset.backTo = 'view-main';
    }
  }

  stopOwnerChatPolling();
  ownerChatClearPendingPhoto(true);
  cancelOwnerChatEdit();
  resetOwnerChatLastSeenId();
  clearOwnerChatMessages();

  if (ownerChatMessagesEl) ownerChatMessagesEl.style.display = '';
  if (ownerChatComposerEl) ownerChatComposerEl.style.display = '';

  if (getIsOwnerApp()) {
    if (!getOwnerChatActiveConversationUserId()) {
      if (ownerChatMessagesEl) ownerChatMessagesEl.style.display = 'none';
      if (ownerChatComposerEl) ownerChatComposerEl.style.display = 'none';
      ownerChatSubtitleEl.textContent = '';
      return;
    }

    if (getOwnerChatMode() === 'order') {
      ownerChatSubtitleEl.textContent = getOwnerChatSelectedDisplayName() || 'Заказ';
    } else if (getOwnerChatSelectedDisplayName()) {
      ownerChatSubtitleEl.textContent = `Диалог с ${getOwnerChatSelectedDisplayName()}`;
    } else {
      ownerChatSubtitleEl.textContent = 'Консультация';
    }
  } else {
    if (getOwnerChatMode() === 'order') {
      if (!getOwnerChatActiveConversationUserId()) {
        const viewerId = getOwnerChatViewerId();
        const orderId = getOwnerChatActiveOrderId();
        if (viewerId && orderId) {
          setOwnerChatActiveConversationUserId(buildOrderChatThreadId(viewerId, orderId));
        }
      }
      ownerChatSubtitleEl.textContent = getOwnerChatSelectedDisplayName() || 'Заказ';
    } else {
      setOwnerChatMode('consult');
      resetOwnerChatOrderContext();
      setOwnerChatActiveConversationUserId(getOwnerChatViewerId());
      setOwnerChatSelectedDisplayName('Консультация');
      ownerChatSubtitleEl.textContent = 'Консультация';
    }
  }

  if (ownerChatLoadingEl) ownerChatLoadingEl.classList.add('chat-loading-spinner--visible');
  try {
    await loadOwnerChatMessages({ reset: true });
  } finally {
    if (ownerChatLoadingEl) ownerChatLoadingEl.classList.remove('chat-loading-spinner--visible');
  }

  subscribeOwnerChatConversation(getOwnerChatActiveConversationUserId());
  startOwnerChatPollFallback();
  scheduleChatKeyboardInset();
}

export { stopOwnerChatPolling } from './websocket.js';

if (ownerChatSendBtn) {
  const sendBtn = ownerChatSendBtn;
  var ownerChatSendViaPointerAt = 0;
  sendBtn.addEventListener(
    'pointerdown',
    function (e) {
      if (sendBtn.disabled) return;
      try {
        e.preventDefault();
      } catch (_) {}
      ownerChatSendViaPointerAt = Date.now();
      sendOwnerChatMessage().catch(function () {});
    },
    { passive: false }
  );
  sendBtn.addEventListener('click', function (e) {
    if (sendBtn.disabled) return;
    if (Date.now() - ownerChatSendViaPointerAt < 700) return;
    try {
      e.preventDefault();
    } catch (_) {}
    sendOwnerChatMessage().catch(function () {});
  });
}

ownerChatSyncSendButtonState();

if (ownerChatAttachBtn && ownerChatPhotoInput) {
  const photoInput = ownerChatPhotoInput;
  ownerChatAttachBtn.addEventListener('click', function () {
    try { photoInput.value = ''; } catch (_) {}
    try { photoInput.click(); } catch (_) {}
  });
}

if (ownerChatPhotoInput) {
  const photoInput = ownerChatPhotoInput;
  ownerChatPhotoInput.addEventListener('change', function () {
    const file = photoInput.files && photoInput.files[0] ? photoInput.files[0] : null;
    if (!file) return;
    const mime = String(file.type || '').trim().toLowerCase();
    if (mime && !/^image\//i.test(mime)) return;
    ownerChatSetPendingPhoto(file);
  });
}

if (ownerChatAttachRemoveBtn) {
  ownerChatAttachRemoveBtn.addEventListener('click', function () {
    ownerChatClearPendingPhoto();
  });
}

if (ownerChatInputEl) {
  ownerChatInputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendOwnerChatMessage().catch(() => {});
    }
  });
  const kbDelays = [0, 50, 120, 250, 400, 600];
  ownerChatInputEl.addEventListener('focus', function () {
    const shouldAvoidScrollTail = getOwnerChatIsSending() || getOwnerChatSuppressKeyboardScroll();
    if (shouldAvoidScrollTail) return;
    if (!getOwnerChatAutoScrollEnabled()) return;
    scrollOwnerChatToBottomSmooth();
    kbDelays.forEach(function (ms) {
      setTimeout(function () {
        scheduleChatKeyboardInset();
        if (getOwnerChatAutoScrollEnabled()) scrollOwnerChatToBottom();
      }, ms);
    });
  });
  ownerChatInputEl.addEventListener('blur', function () {
    setTimeout(scheduleChatKeyboardInset, 50);
    setTimeout(scheduleChatKeyboardInset, 200);
    setTimeout(scheduleChatKeyboardInset, 400);
  });
}
